import type {
	ContextBindingConfig,
	FunctionBindingConfig,
	TemplateBindingConfig,
	FunctionBindingItem,
	ContextBindingItem,
	StaticArgItem,
} from '@wix/thunderbolt-becky-types'
import type { BindingConfig, ContextProviderRegistry, FunctionLibraryRegistry } from './types'

/**
 * Checks if the given value is a wix/signals-service signal
 */
const isSignal = (value: any) => {
	return typeof value?.peek === 'function' && typeof value?.get === 'function'
}

/** retrieve deeply nested value, unwrapping signals along the path */
const getSignalValueDeep = (target: any, path: string) => {
	let value = target
	for (const pathSegment of path.split('.')) {
		if (value) {
			const maybeSignal = value[pathSegment]
			value = isSignal(maybeSignal) ? maybeSignal.get() : maybeSignal
		}
	}
	return value
}

const cloneDeep = (obj: any): any => JSON.parse(JSON.stringify(obj))

// Top-level bindings must distinguish "function is missing" from "function ran and returned undefined":
// missing functions skip prop assignment, while undefined return values are assigned as the bound value.
type FunctionBindingResolution =
	| {
			resolved: true
			value: unknown
	  }
	| {
			resolved: false
	  }

/** this is a much simpler version of _.set since we don't need the extra complexity, and we want to max out render performance,
 * this method returns a deep copy of the original target object */
export const setDeep = (target: any, path: Array<string>, value: any) => {
	// replace the instance so react will re-render the component
	const root = cloneDeep(target)
	let curr = root
	for (let i = 0; i < path.length; i++) {
		if (i === path.length - 1) {
			const fieldName = path[i]
			curr[fieldName] = value
		} else {
			const pathSegment = path[i]
			if (!(pathSegment in curr)) {
				curr[pathSegment] = {}
			}
			curr = curr[pathSegment]
		}
	}
	return root
}

/** Resolve a single function binding argument to its runtime value */
const resolveArgument = (
	arg: FunctionBindingItem | ContextBindingItem | StaticArgItem,
	contextProviderRegistry: ContextProviderRegistry,
	functionLibraryRegistry: FunctionLibraryRegistry | undefined,
	debug?: boolean
): unknown => {
	switch (arg.type) {
		case 'StaticArgItem':
			return arg.value
		case 'ContextBindingItem': {
			const getContext = contextProviderRegistry.getUseContextHook(arg.contextProvider)
			if (!getContext) {
				return undefined
			}
			const context = getContext()
			if (arg.contextItem.bindingItemType === 'ContextItem') {
				return getSignalValueDeep(context, arg.contextItem.itemPath)
			}
			return undefined
		}
		case 'FunctionBindingItem': {
			const resolution = resolveFunctionBindingItem(arg, contextProviderRegistry, functionLibraryRegistry, debug)
			// Nested arguments pass undefined in both cases: missing nested function or valid undefined return value.
			return resolution.resolved ? resolution.value : undefined
		}
		default:
			return undefined
	}
}

const resolveFunctionBindingItem = (
	functionBindingItem: FunctionBindingItem,
	contextProviderRegistry: ContextProviderRegistry,
	functionLibraryRegistry: FunctionLibraryRegistry | undefined,
	debug?: boolean,
	propertyName?: string
): FunctionBindingResolution => {
	if (!functionLibraryRegistry) {
		if (debug && propertyName) {
			console.warn(`[FunctionBinding] No function library registry — skipping binding for "${propertyName}"`)
		}
		return { resolved: false }
	}

	const fn = functionLibraryRegistry.getFunction(functionBindingItem.functionLibraryType, functionBindingItem.function)
	if (!fn) {
		if (debug) {
			console.warn(
				`[FunctionBinding] Function "${functionBindingItem.function}" not found in library "${functionBindingItem.functionLibraryType}" — skipping`
			)
		}
		return { resolved: false }
	}

	const args = (functionBindingItem.arguments || []).map((arg) =>
		resolveArgument(arg, contextProviderRegistry, functionLibraryRegistry, debug)
	)
	try {
		// Match top-level FunctionBindingConfig semantics for nested FunctionBindingItem arguments.
		if (functionBindingItem.asCallback) {
			return {
				resolved: true,
				value: (...extraArgs: Array<unknown>) => fn(...args, ...extraArgs),
			}
		}
		return {
			resolved: true,
			value: fn(...args),
		}
	} catch (e) {
		console.error(
			`Failed to execute function "${functionBindingItem.function}" from library "${functionBindingItem.functionLibraryType}"`,
			`args: [${args.map((a) => (typeof a === 'string' ? `"${a}"` : String(a))).join(', ')}]`,
			`binding: ${JSON.stringify({ propertyName, asCallback: functionBindingItem.asCallback, arguments: functionBindingItem.arguments?.map((a) => ({ type: a.type })) })}`,
			e
		)
		return { resolved: false }
	}
}

/** Handle a ContextBindingConfig — resolves context values and methods to component props */
const handleContextBinding = (
	binding: ContextBindingConfig,
	contextProviderRegistry: ContextProviderRegistry,
	agg: { [prop: string]: unknown },
	debug?: boolean
) => {
	const getContext = contextProviderRegistry.getUseContextHook(binding.contextProvider)
	if (!getContext) {
		if (debug) {
			console.warn(
				`[ContextBinding] Context provider "${binding.contextProvider}" not found in registry — skipping binding`
			)
		}
		return
	}

	const context = getContext()
	if (debug) {
		console.log(
			`[ContextBinding] Resolving ${Object.keys(binding.properties).length} props from "${binding.contextProvider}"`,
			{ contextKeys: context ? Object.keys(context) : 'null' }
		)
	}

	Object.entries(binding.properties).forEach(([compProp, bindingProperty]) => {
		try {
			// Support both "ContextMethod" (current) and "Method" (deprecated) binding types
			const itemType = (bindingProperty as any).bindingItemType

			switch (itemType) {
				case 'ContextItem': {
					const ctxItem = bindingProperty as any
					const value = getSignalValueDeep(context, ctxItem.itemPath)
					if (debug) {
						console.log(
							`[ContextBinding]   ${compProp} ← context.${ctxItem.itemPath} = ${typeof value === 'function' ? '[Function]' : JSON.stringify(value)?.slice(0, 80)}`
						)
					}
					agg[compProp] = value
					break
				}
				case 'ContextMethod':
				case 'Method': {
					const methodBinding = bindingProperty as any
					const method = getSignalValueDeep(context, methodBinding.methodPath)
					if (typeof method !== 'function') {
						if (debug) {
							console.warn(
								`[ContextBinding]   ${compProp} ← context.${methodBinding.methodPath} — NOT a function (got ${typeof method}), skipping`
							)
						}
						break
					}
					const args = (methodBinding.callOptions.arguments || []).map((arg: any) =>
						resolveArgument(arg, contextProviderRegistry, undefined, debug)
					)
					if (methodBinding.callOptions.asCallback) {
						agg[compProp] = (...extraArgs: Array<unknown>) => method(...args, ...extraArgs)
						if (debug) {
							console.log(
								`[ContextBinding]   ${compProp} ← context.${methodBinding.methodPath}(${args.length} args) [callback]`
							)
						}
					} else {
						const result = method(...args)
						agg[compProp] = result
						if (debug) {
							console.log(
								`[ContextBinding]   ${compProp} ← context.${methodBinding.methodPath}(${args.map((a: any) => (typeof a === 'string' ? `"${a}"` : String(a))).join(', ')}) = ${typeof result === 'function' ? '[Function]' : JSON.stringify(result)?.slice(0, 80)}`
							)
						}
					}
					break
				}
				default:
					if (debug) {
						console.warn(
							`[ContextBinding]   ${compProp} — unknown bindingItemType: "${(bindingProperty as any).bindingItemType}"`,
							bindingProperty
						)
					}
			}
		} catch (e) {
			console.error(`[ContextBinding]   ${compProp} ← FAILED from context "${binding.contextProvider}"`, e)
		}
	})
}

/** Handle a FunctionBindingConfig — calls a function from a function library and binds the result to a prop */
const handleFunctionBinding = (
	binding: FunctionBindingConfig,
	contextProviderRegistry: ContextProviderRegistry,
	functionLibraryRegistry: FunctionLibraryRegistry | undefined,
	agg: { [prop: string]: unknown },
	debug?: boolean
) => {
	const { propertyName, functionBindingItem } = binding
	const resolution = resolveFunctionBindingItem(
		functionBindingItem,
		contextProviderRegistry,
		functionLibraryRegistry,
		debug,
		propertyName
	)
	if (resolution.resolved) {
		agg[propertyName] = resolution.value
	}
}

/**
 * Handle a TemplateBindingConfig — resolves item fields from the template item
 * passed by the repeater's render function.
 *
 * The item flows from the repeater via React Context (TemplateItemContext):
 * useRenderProps wraps each template instance with a Provider carrying { item, index },
 * and ContextBindingWrapper reads it via useContext and passes it here.
 *
 * binding.listItemPath is edit-time metadata only — it tells the editor which array
 * prop the template iterates over, but is not enforced at runtime. The viewer trusts
 * the item the repeater passed to the render function.
 */
const handleTemplateBinding = (
	binding: TemplateBindingConfig,
	templateItem: unknown,
	agg: { [prop: string]: unknown },
	debug?: boolean
) => {
	if (templateItem == null) {
		if (debug) {
			console.warn(
				`[TemplateBinding] No template item provided — skipping template binding "${binding.id}". ` +
					`This usually means the component is not rendered inside a repeater's template render function.`
			)
		}
		return
	}

	Object.entries(binding.properties).forEach(([compProp, bindingProperty]) => {
		const itemType = (bindingProperty as { bindingItemType: string }).bindingItemType
		switch (itemType) {
			case 'TemplateItem': {
				const value = getSignalValueDeep(templateItem, bindingProperty.itemPath)
				if (debug) {
					console.log(
						`[TemplateBinding]   ${compProp} ← item.${bindingProperty.itemPath} = ${JSON.stringify(value)?.slice(0, 80)}`
					)
				}
				agg[compProp] = value
				break
			}
			default:
				// TemplateMethod / TemplateIndex / TemplateLength are in the DM spec but
				// not yet implemented. Warn so misconfigurations aren't silent.
				console.warn(
					`[TemplateBinding]   ${compProp} — unsupported bindingItemType: "${itemType}" (binding "${binding.id}")`
				)
		}
	})
}

/** Retrieves the bound field value from the target context / function and set it under the binding prop name key */
export const getBoundProps = (
	bindings: Array<BindingConfig>,
	contextProviderRegistry: ContextProviderRegistry,
	functionLibraryRegistry?: FunctionLibraryRegistry,
	debug?: boolean,
	templateItem?: unknown
): { [prop: string]: unknown } => {
	return bindings.reduce<{ [prop: string]: unknown }>((agg, binding) => {
		try {
			if (binding.type === 'ContextBindingConfig') {
				handleContextBinding(binding, contextProviderRegistry, agg, debug)
			} else if (binding.type === 'FunctionBindingConfig') {
				handleFunctionBinding(binding, contextProviderRegistry, functionLibraryRegistry, agg, debug)
			} else if (binding.type === 'TemplateBindingConfig') {
				handleTemplateBinding(binding, templateItem, agg, debug)
			}
		} catch (error) {
			console.error('Failed to collect bound props from binding', binding, error)
		}
		return agg
	}, {})
}
